// (c) 2026 Planet Q Productions. All Rights Reserved.

import React, { useRef, useEffect } from 'react';

const PuzzlerMode = ({
  backgroundImage,
  characterImage,
  enemyImage,
  gameTitle,
  isPlaying,
  onScoreUpdate,
  onGameOver,
}) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const imagesRef = useRef({});

  useEffect(() => {
    const loadImg = (src) => new Promise(res => {
      if (!src) { res(null); return; }
      const img = new Image();
      if (src.startsWith('http')) img.crossOrigin = 'anonymous';
      img.onload = () => res(img); img.onerror = () => res(null);
      img.src = src;
    });
    Promise.all([
      loadImg(backgroundImage), loadImg(characterImage), loadImg(enemyImage),
    ]).then(([bg, ch, en]) => { imagesRef.current = { bg, ch, en }; });
  }, [backgroundImage, characterImage, enemyImage]);

  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const width = canvas.width = canvas.offsetWidth;
    const height = canvas.height = canvas.offsetHeight;

    const COLS = 6, ROWS = 8;
    const cellSize = Math.min((width - 40) / COLS, (height - 120) / ROWS);
    const boardX = (width - COLS * cellSize) / 2;
    const boardY = 60;
    const colors = ['#FF4444', '#44FF44', '#4444FF', '#FFFF44', '#FF44FF', '#44FFFF'];

    // Generate board
    const board = [];
    for (let r = 0; r < ROWS; r++) {
      board[r] = [];
      for (let c = 0; c < COLS; c++) {
        board[r][c] = { color: Math.floor(Math.random() * colors.length), glow: 0, falling: 0 };
      }
    }

    const s = {
      frame: 0, score: 0, targetScore: 300,
      cursorR: 0, cursorC: 0, selected: null,
      swapping: false, swapTimer: 0,
      checking: false, clearing: false,
      gameOver: false, movesLeft: 30,
      mascotAnim: 0, mascotMood: 'happy',
      comboCount: 0, particles: [],
    };

    const keys = {};
    const onKeyDown = (e) => {
      keys[e.key] = true;
      if (s.swapping || s.clearing || s.gameOver) return;
      if (e.key === 'ArrowUp') s.cursorR = Math.max(0, s.cursorR - 1);
      if (e.key === 'ArrowDown') s.cursorR = Math.min(ROWS - 1, s.cursorR + 1);
      if (e.key === 'ArrowLeft') s.cursorC = Math.max(0, s.cursorC - 1);
      if (e.key === 'ArrowRight') s.cursorC = Math.min(COLS - 1, s.cursorC + 1);
      if (e.key === ' ' || e.key === 'Enter') {
        if (!s.selected) {
          s.selected = { r: s.cursorR, c: s.cursorC };
        } else {
          const dr = Math.abs(s.selected.r - s.cursorR);
          const dc = Math.abs(s.selected.c - s.cursorC);
          if ((dr === 1 && dc === 0) || (dr === 0 && dc === 1)) {
            // Swap
            const temp = board[s.selected.r][s.selected.c].color;
            board[s.selected.r][s.selected.c].color = board[s.cursorR][s.cursorC].color;
            board[s.cursorR][s.cursorC].color = temp;
            s.movesLeft--;
            s.comboCount = 0;
            checkMatches();
          }
          s.selected = null;
        }
      }
    };
    const onKeyUp = (e) => { keys[e.key] = false; };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.gameActions = {
      moveUp: () => { s.cursorR = Math.max(0, s.cursorR - 1); },
      moveDown: () => { s.cursorR = Math.min(ROWS - 1, s.cursorR + 1); },
      moveLeft: () => { s.cursorC = Math.max(0, s.cursorC - 1); },
      moveRight: () => { s.cursorC = Math.min(COLS - 1, s.cursorC + 1); },
      shoot: () => {
        if (s.swapping || s.clearing || s.gameOver) return;
        if (!s.selected) {
          s.selected = { r: s.cursorR, c: s.cursorC };
        } else {
          const dr = Math.abs(s.selected.r - s.cursorR);
          const dc = Math.abs(s.selected.c - s.cursorC);
          if ((dr === 1 && dc === 0) || (dr === 0 && dc === 1)) {
            const temp = board[s.selected.r][s.selected.c].color;
            board[s.selected.r][s.selected.c].color = board[s.cursorR][s.cursorC].color;
            board[s.cursorR][s.cursorC].color = temp;
            s.movesLeft--;
            s.comboCount = 0;
            checkMatches();
          }
          s.selected = null;
        }
      },
    };

    function checkMatches() {
      let matched = false;
      const toRemove = new Set();
      // Horizontal
      for (let r = 0; r < ROWS; r++) {
        for (let c = 0; c <= COLS - 3; c++) {
          if (board[r][c].color === board[r][c+1].color && board[r][c].color === board[r][c+2].color && board[r][c].color >= 0) {
            toRemove.add(`${r},${c}`); toRemove.add(`${r},${c+1}`); toRemove.add(`${r},${c+2}`);
          }
        }
      }
      // Vertical
      for (let c = 0; c < COLS; c++) {
        for (let r = 0; r <= ROWS - 3; r++) {
          if (board[r][c].color === board[r+1][c].color && board[r][c].color === board[r+2][c].color && board[r][c].color >= 0) {
            toRemove.add(`${r},${c}`); toRemove.add(`${r+1},${c}`); toRemove.add(`${r+2},${c}`);
          }
        }
      }

      if (toRemove.size > 0) {
        matched = true;
        s.comboCount++;
        const points = toRemove.size * 10 * s.comboCount;
        s.score += points;
        s.mascotMood = s.comboCount > 1 ? 'excited' : 'happy';
        // Particles
        toRemove.forEach(key => {
          const [r, c] = key.split(',').map(Number);
          const px = boardX + c * cellSize + cellSize / 2;
          const py = boardY + r * cellSize + cellSize / 2;
          for (let i = 0; i < 4; i++) {
            s.particles.push({
              x: px, y: py,
              vx: (Math.random() - 0.5) * 8, vy: (Math.random() - 0.5) * 8,
              life: 25, color: colors[board[r][c].color] || '#fff',
            });
          }
          board[r][c].color = -1; // mark removed
        });
        // Gravity - after delay
        setTimeout(() => {
          for (let c = 0; c < COLS; c++) {
            let writeR = ROWS - 1;
            for (let r = ROWS - 1; r >= 0; r--) {
              if (board[r][c].color >= 0) {
                board[writeR][c].color = board[r][c].color;
                if (writeR !== r) board[r][c].color = -1;
                writeR--;
              }
            }
            for (let r = writeR; r >= 0; r--) {
              board[r][c].color = Math.floor(Math.random() * colors.length);
              board[r][c].falling = 10;
            }
          }
          // Check for cascading matches
          setTimeout(() => checkMatches(), 200);
        }, 300);
      } else {
        s.comboCount = 0;
        s.mascotMood = 'neutral';
      }

      if (onScoreUpdate) onScoreUpdate(s.score);

      // Game over check
      if (!matched && s.movesLeft <= 0) {
        s.gameOver = true;
      }
    }

    const loop = () => {
      s.frame++;
      s.mascotAnim++;
      const images = imagesRef.current;
      ctx.clearRect(0, 0, width, height);

      if (s.gameOver) {
        ctx.fillStyle = 'rgba(0,0,0,0.7)'; ctx.fillRect(0, 0, width, height);
        if (onGameOver) onGameOver(s.score);
        return;
      }

      // Background
      if (images.bg) {
        ctx.globalAlpha = 0.4;
        ctx.drawImage(images.bg, 0, 0, width, height);
        ctx.globalAlpha = 1;
      }
      // Gradient overlay
      const grad = ctx.createLinearGradient(0, 0, 0, height);
      grad.addColorStop(0, 'rgba(20,10,40,0.7)'); grad.addColorStop(1, 'rgba(10,5,30,0.9)');
      ctx.fillStyle = grad; ctx.fillRect(0, 0, width, height);

      // Board background
      ctx.fillStyle = 'rgba(0,0,0,0.3)';
      ctx.fillRect(boardX - 5, boardY - 5, COLS * cellSize + 10, ROWS * cellSize + 10);

      // Draw cells
      for (let r = 0; r < ROWS; r++) {
        for (let c = 0; c < COLS; c++) {
          const cell = board[r][c];
          const cx = boardX + c * cellSize;
          const cy = boardY + r * cellSize;
          if (cell.falling > 0) cell.falling--;
          const fallOff = cell.falling * 3;

          if (cell.color >= 0) {
            const pulse = 1 + Math.sin(s.frame * 0.05 + r + c) * 0.03;
            const size = (cellSize - 4) * pulse;
            const offset = (cellSize - size) / 2;
            ctx.fillStyle = colors[cell.color];
            ctx.shadowColor = colors[cell.color];
            ctx.shadowBlur = 6;
            ctx.beginPath();
            ctx.roundRect(cx + offset, cy + offset - fallOff, size, size, 6);
            ctx.fill();
            ctx.shadowBlur = 0;

            // Inner shine
            ctx.fillStyle = 'rgba(255,255,255,0.2)';
            ctx.beginPath();
            ctx.roundRect(cx + offset + 3, cy + offset + 3 - fallOff, size * 0.4, size * 0.3, 3);
            ctx.fill();
          }
        }
      }

      // Cursor
      const cx = boardX + s.cursorC * cellSize;
      const cy = boardY + s.cursorR * cellSize;
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 3;
      ctx.strokeRect(cx + 1, cy + 1, cellSize - 2, cellSize - 2);

      // Selected highlight
      if (s.selected) {
        const sx = boardX + s.selected.c * cellSize;
        const sy = boardY + s.selected.r * cellSize;
        ctx.strokeStyle = '#FFD700';
        ctx.lineWidth = 3;
        ctx.setLineDash([5, 3]);
        ctx.strokeRect(sx + 1, sy + 1, cellSize - 2, cellSize - 2);
        ctx.setLineDash([]);
      }

      // Mascot character (bottom right)
      const mascotX = width - 60;
      const mascotY = height - 50;
      const mascotSize = 40;
      if (images.ch) {
        ctx.save();
        ctx.translate(mascotX, mascotY);
        const bounce = Math.sin(s.mascotAnim * 0.08) * (s.mascotMood === 'excited' ? 8 : 3);
        const tilt = Math.sin(s.mascotAnim * 0.06) * (s.mascotMood === 'excited' ? 0.15 : 0.05);
        ctx.rotate(tilt);
        ctx.drawImage(images.ch, -mascotSize / 2, -mascotSize + bounce, mascotSize, mascotSize);
        ctx.restore();
      }

      // Particles
      s.particles = s.particles.filter(p => p.life > 0);
      s.particles.forEach(p => {
        p.x += p.vx; p.y += p.vy; p.vy += 0.2; p.life--;
        ctx.globalAlpha = p.life / 25;
        ctx.fillStyle = p.color;
        ctx.beginPath(); ctx.arc(p.x, p.y, 3, 0, Math.PI * 2); ctx.fill();
      });
      ctx.globalAlpha = 1;

      // HUD
      const hudY = boardY + ROWS * cellSize + 20;
      ctx.fillStyle = '#fff'; ctx.font = 'bold 16px Rajdhani, sans-serif'; ctx.textAlign = 'left';
      ctx.fillText(`SCORE: ${s.score}`, boardX, hudY);
      ctx.fillStyle = s.movesLeft <= 5 ? '#f44' : '#fff';
      ctx.textAlign = 'right';
      ctx.fillText(`MOVES: ${s.movesLeft}`, boardX + COLS * cellSize, hudY);
      // Combo indicator
      if (s.comboCount > 1) {
        ctx.fillStyle = '#FFD700'; ctx.font = 'bold 20px Rajdhani, sans-serif'; ctx.textAlign = 'center';
        ctx.fillText(`COMBO x${s.comboCount}!`, width / 2, hudY + 25);
      }
      // Target score
      const barW = COLS * cellSize;
      ctx.fillStyle = 'rgba(255,255,255,0.15)';
      ctx.fillRect(boardX, hudY + 8, barW, 6);
      ctx.fillStyle = '#0ff';
      ctx.fillRect(boardX, hudY + 8, barW * Math.min(s.score / s.targetScore, 1), 6);

      animationRef.current = requestAnimationFrame(loop);
    };

    animationRef.current = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      delete window.gameActions;
    };
  }, [isPlaying, onScoreUpdate, onGameOver]);

  return (
    <canvas
      ref={canvasRef}
      data-testid="puzzler-canvas"
      className="absolute inset-0 w-full h-full"
      style={{ touchAction: 'none' }}
    />
  );
};

export default PuzzlerMode;
